/**
 * Engage — Tabbed Layout
 *
 * Routes:
 *   /engage/audiences  → EngageAudiences
 *   /engage/sms        → EngageSMS
 *   /engage/email      → EngageEmail
 *   /engage/history    → EngageHistory
 *
 * The layout shows the page header, a Suggested Campaigns panel, and a tab bar.
 * Child pages render via <Outlet />.
 */
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Megaphone, Users, MessageSquare, Mail, History, Sparkles, ArrowRight, Zap, Star, RefreshCw, Tag, MessageCircle, CalendarDays, Loader2 } from 'lucide-react';
import { auth } from '../firebase';

const TABS = [
  { href: 'audiences', label: 'Audiences',      icon: Users         },
  { href: 'sms',       label: 'SMS Campaigns',  icon: MessageSquare },
  { href: 'email',     label: 'Email',           icon: Mail          },
  { href: 'history',   label: 'History',         icon: History       },
];

// ── Intent icon + color map ────────────────────────────────────────────────────

const INTENT_STYLES = {
  quote_followup:  { icon: MessageCircle, bg: 'bg-blue-100',   text: 'text-blue-600',   border: 'border-blue-200'   },
  review_request:  { icon: Star,          bg: 'bg-yellow-100', text: 'text-yellow-600', border: 'border-yellow-200' },
  reactivation:    { icon: RefreshCw,     bg: 'bg-green-100',  text: 'text-green-600',  border: 'border-green-200'  },
  seasonal_promo:  { icon: CalendarDays,  bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
  custom:          { icon: Tag,           bg: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-200' },
};

// ── Suggested Campaigns panel ──────────────────────────────────────────────────

const SuggestedCampaigns = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const fetchSuggestions = async () => {
      try {
        const token = await auth.currentUser?.getIdToken(false);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/suggestions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSuggestions(data || []);
      } catch (err) {
        console.warn('[SuggestedCampaigns] fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSuggestions();
    return () => { cancelled = true; };
  }, []);

  const handleGenerate = (suggestion) => {
    navigate('/engage/email', {
      state: {
        openModal: true,
        suggestedIntent: suggestion.intent,
        suggestedExtra: suggestion.extraContext,
      },
    });
  };

  // Don't render panel if loading is done and there are no suggestions
  if (!loading && suggestions.length === 0) return null;

  return (
    <div className="mb-6" data-testid="suggested-campaigns-panel">
      {/* Panel header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
          <Zap className="w-3.5 h-3.5 text-white" />
        </div>
        <h2 className="text-sm font-semibold text-gray-800">Suggested Campaigns</h2>
        <span className="text-xs text-gray-400">Based on your recent activity</span>
      </div>

      {/* Cards row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-200" />
                  <div className="h-3.5 bg-gray-200 rounded w-24" />
                </div>
                <div className="h-3 bg-gray-100 rounded w-full mb-1.5" />
                <div className="h-3 bg-gray-100 rounded w-3/4 mb-3" />
                <div className="h-6 bg-gray-100 rounded w-20" />
              </div>
            ))
          : suggestions.map((s) => {
              const style = INTENT_STYLES[s.intent] || INTENT_STYLES.custom;
              const Icon = style.icon;
              return (
                <div
                  key={s.id}
                  className={`bg-white border ${style.border} rounded-xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow`}
                  data-testid={`suggestion-card-${s.id}`}
                >
                  {/* Icon + title */}
                  <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg}`}>
                      <Icon className={`w-4 h-4 ${style.text}`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 leading-snug">{s.title}</p>
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 text-xs font-medium rounded-full ${style.bg} ${style.text}`}>
                        {s.audienceSize} contact{s.audienceSize !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-gray-500 leading-relaxed flex-1">{s.description}</p>

                  {/* CTA */}
                  <button
                    onClick={() => handleGenerate(s)}
                    className={`flex items-center gap-1.5 text-xs font-semibold ${style.text} hover:gap-2.5 transition-all group`}
                    data-testid={`suggestion-generate-btn-${s.id}`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Generate Email
                    <ArrowRight className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
                  </button>
                </div>
              );
            })}
      </div>
    </div>
  );
};

// ── Main Engage layout ─────────────────────────────────────────────────────────

const Engage = () => {
  const { pathname } = useLocation();

  // Redirect /engage → /engage/audiences
  if (pathname === '/engage' || pathname === '/engage/') {
    return <Navigate to="/engage/audiences" replace />;
  }

  return (
    <div className="max-w-5xl mx-auto pb-12">

      {/* Page header */}
      <div className="flex items-start gap-4 pt-4 pb-6">
        <div className="w-12 h-12 rounded-full bg-[rgba(242,107,29,0.1)] flex items-center justify-center shrink-0">
          <Megaphone className="w-6 h-6 text-[var(--cb-orange)]" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Engage</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Reach your leads and clients with targeted SMS, email, and content.
          </p>
        </div>
      </div>

      {/* Suggested Campaigns panel */}
      <SuggestedCampaigns />

      {/* Tab bar */}
      <div
        className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit"
        data-testid="engage-tab-bar"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <NavLink
              key={tab.href}
              to={`/engage/${tab.href}`}
              data-testid={`engage-tab-${tab.href}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${isActive
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </NavLink>
          );
        })}
      </div>

      {/* Active child page */}
      <Outlet />
    </div>
  );
};

export default Engage;
